import { Check, X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The answer selection control for the knowledge-check block — a 24px control:
 * single-choice = circle ("radio"), multiple-choice = rounded square
 * ("checkbox"). Every colour is a lomda theme variable (STANDARDS-LOMDA-V3.md
 * §3). The radio ring + dot are drawn as an SVG so the exact Figma sizes
 * (1.5px ring, 12.8px dot) stay perfectly centred and even — a 1.5px CSS border
 * renders lopsided on a 24px circle (the browser can't split 1.5px evenly).
 * Figma: radio checked = primary ring + primary dot (unfilled); checkbox
 * checked = filled primary + card ✓; idle = light ring / border.
 */
export const CHOICE_CONTROL = {
    size: "size-6", // 24px
    checkRadius: "rounded-[4px]", // multi-choice square
    markSize: "size-4", // ✓ / ✗ mark
    ringWidth: 1.5, // Figma 1.5px
    dotDiameter: 12.8, // Figma 12.8px
    primary: "hsl(var(--lomda-primary))",
    idleBorder: "hsl(var(--lomda-border))",
    mark: "hsl(var(--lomda-card))",
    success: "hsl(var(--lomda-success))",
    error: "hsl(var(--lomda-destructive))",
} as const;

type ChoiceVariant = "radio" | "checkbox";
type ChoiceStatus = "off" | "on" | "correct" | "wrong";

export function ChoiceControl({
    variant,
    status,
    className,
}: {
    variant: ChoiceVariant;
    status: ChoiceStatus;
    className?: string;
}) {
    // Revealed grading (both variants): solid fill + icon.
    if (status === "correct" || status === "wrong") {
        const fill =
            status === "correct" ? CHOICE_CONTROL.success : CHOICE_CONTROL.error;
        const Icon = status === "correct" ? Check : X;
        const shape =
            variant === "radio" ? "rounded-full" : CHOICE_CONTROL.checkRadius;
        return (
            <span
                className={cn(
                    "flex shrink-0 items-center justify-center",
                    CHOICE_CONTROL.size,
                    shape,
                    className
                )}
                style={{ backgroundColor: fill, color: CHOICE_CONTROL.mark }}
            >
                <Icon className={CHOICE_CONTROL.markSize} strokeWidth={2.5} />
            </span>
        );
    }

    // Single-choice: SVG ring (+ dot) so the 1.5px stroke and 12.8px dot render
    // symmetric and dead-centre — a thin CSS border is lopsided at 24px.
    if (variant === "radio") {
        const ring =
            status === "on" ? CHOICE_CONTROL.primary : CHOICE_CONTROL.idleBorder;
        return (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                className={cn("shrink-0", className)}
                aria-hidden
            >
                <circle
                    cx="12"
                    cy="12"
                    r={(24 - CHOICE_CONTROL.ringWidth) / 2}
                    fill="none"
                    stroke={ring}
                    strokeWidth={CHOICE_CONTROL.ringWidth}
                />
                {status === "on" && (
                    <circle
                        cx="12"
                        cy="12"
                        r={CHOICE_CONTROL.dotDiameter / 2}
                        fill={CHOICE_CONTROL.primary}
                    />
                )}
            </svg>
        );
    }

    // Multiple-choice checkbox — rounded square.
    const boxBase = cn(
        "flex shrink-0 items-center justify-center",
        CHOICE_CONTROL.size,
        CHOICE_CONTROL.checkRadius,
        className
    );
    if (status === "on") {
        return (
            <span
                className={boxBase}
                style={{
                    backgroundColor: CHOICE_CONTROL.primary,
                    color: CHOICE_CONTROL.mark,
                }}
            >
                <Check className={CHOICE_CONTROL.markSize} strokeWidth={2.5} />
            </span>
        );
    }
    return (
        <span
            className={cn(boxBase, "border-[1.5px]")}
            style={{ borderColor: CHOICE_CONTROL.idleBorder }}
        />
    );
}
